import { useState, FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { colors, gradients, glass } from '../styles/design-tokens';

interface WaitlistFormProps {
  source?: string;
  compact?: boolean;
}

export default function WaitlistForm({ source = 'website', compact = false }: WaitlistFormProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const addToWaitlist = httpsCallable(functions, 'addToWaitlist');
      const result = await addToWaitlist({ email, name: name || null, source });
      const data = result.data as { success: boolean; alreadyExists?: boolean };

      if (data.success) {
        setSuccess(true);
      }
    } catch (err: unknown) {
      const errorObj = err as { message?: string };
      setError(errorObj.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="rounded-xl p-6 text-center"
        style={{ background: glass.background, border: glass.border }}
      >
        <div className="text-2xl mb-2" style={{ color: colors.gold }}>&#10003;</div>
        <h3 className="text-lg font-semibold mb-1" style={{ color: colors.cream }}>
          You're on the list
        </h3>
        <p className="text-sm" style={{ color: colors.textSecondary }}>
          We'll notify you when NorthStar Aurora is ready for you.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={compact ? 'space-y-3' : 'space-y-4'}>
      {!compact && (
        <input
          type="text"
          placeholder="Name (optional)"
          value={name}
          onChange={e => setName(e.target.value)}
          className="w-full px-4 py-3 rounded-lg outline-none transition focus:ring-1"
          style={{
            background: glass.backgroundDark,
            border: glass.border,
            color: colors.cream,
          }}
        />
      )}
      <div className={compact ? 'flex gap-2' : ''}>
        <input
          type="email"
          placeholder="Enter your email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          className={`${compact ? 'flex-1' : 'w-full'} px-4 py-3 rounded-lg outline-none transition focus:ring-1`}
          style={{
            background: glass.backgroundDark,
            border: glass.border,
            color: colors.cream,
          }}
        />
        <button
          type="submit"
          disabled={loading}
          className={`${compact ? '' : 'w-full mt-3'} px-6 py-3 rounded-lg font-semibold text-sm transition hover:opacity-90 disabled:opacity-50`}
          style={{
            background: gradients.goldButton,
            color: colors.navy,
            boxShadow: '0 8px 32px rgba(212, 175, 55, 0.25)',
          }}
        >
          {loading ? 'Joining...' : 'Join Waitlist'}
        </button>
      </div>
      {error && (
        <p className="text-sm text-center" style={{ color: colors.cranberry }}>{error}</p>
      )}
    </form>
  );
}
